import { useQuery, useMutation} from '@tanstack/react-query'
import { useEffect, startTransition } from 'react'
import { 
    addResourcePlan, getKitchenID, addNewResource, deleteCommunityResource,
    getAllResources, getResourceByID, updateResourceById, deleteMembershipPlan,
    addKitchen, updateKitchen, updatePlan, getMembershipPlan, updateRulesForResource, 
    updateKitchenOptions, getCommunityAllInventory,
    deleteMembershipRequirement, addMembershipRequirement, addRequirements,
    addCommunityPlans, updatePlansForResource, getCommunityRequirements,
    addResourceInventory,updateResourceInventory, getAllResourceInventory, updateRequirementsForResource,
    deleteInventoryItemById, updateResourceOptions, updateResourceFee, 
    deleteResourceDiscount, updateResourceDiscount, deleteResourceFee   } from '../api/crud'
import { updateResourceSteps } from '../api/documentationSteps'

    import { 
    InventorySchema, KitchenOptionsType, KitchenSpaceSchema, 
    ResourcePlanSchema, PlanSchema, RequirementSchema, InventoryCatalogSuggestion, ResourceSchema, 
    ResourceType, PlanType, SettingsOptionsType, ResourceOptionType, TrainingInventorySchema, 
    FeeSchema, DiscountSchema, DocumentationStep } from "../data/types";

import { useResourceState } from '../contexts/kitchencontext'
import { searchInventoryCatalog as searchIndexedInventoryCatalog } from '../utils/helpers/inventoryCatalogSearch';
// import { ReactComponent as PreferenceIcon } from '../icons/PreferenceIcon.svg'
import { ReactComponent as FundingIcon } from '../icons/fundingIcon.svg'
import { ReactComponent as KitchenIcon } from '../icons/KitchenIcon.svg'
// import { ReactComponent as NotificationIcon } from '../icons/NotificationIcon.svg'
// import { ReactComponent as AccountIcon } from '../icons/Account.svg'
import { ReactComponent as ExpertIcon } from '../icons/expertIcon.svg'
import { ReactComponent as TrainingIcon } from '../icons/TrainingIcon.svg'
import { ReactComponent as OrderIcon } from '../icons/OrderIcon.svg'

import { ReactComponent as CommunityIcon } from '../icons/Community.svg'

import { ReactComponent as DocumentationIcon } from '../icons/documentationIcon.svg'
import { useAuthState } from '../contexts/authcontext';
import { useLoadPlans } from './communityhook';

const staleTime = 1000
const resourcesStaleTime = 5 * 60 * 1000
const isEnabled = true


export const useLoadCommunityResources = () => {
    var { communityId, } = useResourceState()
    communityId = communityId ? communityId : "resource_pantry"
    const {data: allResources, refetch: reloadResources,  isLoading: isLoadingResources, isError: isErrorResources, error} = useQuery({ 
        queryKey: ['resources', communityId], 
        queryFn: async () => {
            if (communityId){
                console.log("fetching resources")
                var res = await getAllResources(communityId)
                return res
            }
        },
        staleTime: resourcesStaleTime,
        refetchOnWindowFocus: false,
        enabled: isEnabled
    })

    useLoadPlans()
    useLoadRequirements()
    if (allResources){
        return {resources: allResources, reloadResources, isErrorResources, error, isLoadingResources}
    }
    return { resources: [], reloadResources, isErrorResources, error, isLoadingResources}
}

/** Resources list only - avoids mounting plans/requirements queries (prevents global context churn). */
export const useLoadCommunityResourcesOnly = (communityIdOverride?: string) => {
    const communityId = communityIdOverride ? communityIdOverride : "resource_pantry"
    const {
        data: allResources,
        refetch: reloadResources,
        isLoading: isLoadingResources,
        isError: isErrorResources,
        error,
    } = useQuery({
        queryKey: ["resources", communityId],
        queryFn: async () => {
            if (communityId) {
                var res = await getAllResources(communityId)
                return res
            }
        },
        staleTime: resourcesStaleTime,
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        enabled: isEnabled,
    })

    return {
        resources: allResources || [],
        reloadResources,
        isErrorResources,
        error,
        isLoadingResources,
    }
}



export const useLoadCurrentKitchen = (kitchenId: string) => {
    let { communityId, setKitchen } = useResourceState()
    const {data: currentKitchen, refetch: reloadCurrentKitchen, isLoading: isLoadingKitchen} = useQuery({ 
        queryKey: ['kitchen', communityId, kitchenId], 
        queryFn: async () => {
            console.log("fetching kitchen by id")
            var res = await getKitchenID({communityId: communityId ? communityId : "", id: kitchenId})
            if (res && setKitchen!== undefined){
                setKitchen(res)
            }
            return res
        },
        staleTime,
        enabled: isEnabled
    })
    var { isLoadingPlans } = useLoadPlans()
    var { isLoadingRequirements } = useLoadRequirements()
    var isLoadingCurrentKitchen = isLoadingKitchen || isLoadingPlans || isLoadingRequirements
    return { isLoadingCurrentKitchen, currentKitchen, reloadCurrentKitchen}
}


export const useLoadCurrentResource = (resourceId: string, queryEnabled = true, syncContext = true) => {
    let { communityId, setCurrentResource } = useResourceState()
    const {data: currentResource, refetch: reloadCurrentResource, isLoading: isLoadingResource} = useQuery({ 
        queryKey: ['resource', resourceId], 
        queryFn: async () => {
            console.log("fetching resource by id")
            communityId = communityId ? communityId : ""
            var res = await getResourceByID(communityId, resourceId)
            if (res && syncContext && setCurrentResource!== undefined){
                setCurrentResource(res)
                
            }
            return res
        },
        staleTime,
        enabled: isEnabled && queryEnabled && Boolean(resourceId)
    })
    return { isLoadingResource, currentResource, reloadCurrentResource}
}


// export const useLoadResources = () => {
//     let { communityId, setResources } = useResourceState()
//     const {data: resources, refetch: reloadResources, isLoading: isLoadingResources} = useQuery({ 
//         queryKey: ["resources", communityId], 
//         queryFn: async () => {
//             if (communityId){
//                 console.log("fetching community resources")
//                 var res = await getAllResources(communityId)
//                 if (res && setResources!== undefined){
//                     setResources(res)
//                 }
//                 return res
//             }
//             return []
//         },
//         staleTime,
//         enabled: isEnabled
//     })
//     return { isLoadingResources, resources, reloadResources}
// }

export const resourcesPerPage: number = 8

export const useLoadResourceOptions = () => {
    const {data: allOptions, refetch: reloadOptions,  isLoading: isLoadingOptions} = useQuery({ 
        queryKey: ['options'], 
        queryFn: async () => {
            console.log("fetching resource options")
            const resourcesOptions: SettingsOptionsType[] = [
                {name: "Local Kitchen Spaces", description: "Discover kitchen communities and food incubators in your area.", icon: KitchenIcon, path: "kitchen", tags: []},
                {name: "Required Documentation & Compliance ", description: "Access essential legal documents and compliance guides", icon: DocumentationIcon, path: "documentation", tags: ["servsafe"]},
                {name: "Culinary Training", description: "Enhance your skills with top culinary training programs.", icon: TrainingIcon, path: "training", tags: ["servsafe"]},
                {name: "Funding & Financial Resources", description: "Discover financial options and resources for your business.", icon: FundingIcon, path: "funding", tags: []},
                {name: "Industry Mentors & Experts", description: "Discover organizations that offer mentorship and industry insights.", icon: ExpertIcon, path: "expert", tags: []},
                {name: "Local Fresh Food", description: "Source fresh, local ingredients for your culinary creations.", icon: OrderIcon, path: "farm", tags: []}
              ]
            return resourcesOptions 
        },
        staleTime,
        enabled: isEnabled
    })

    const searchOptions = (searchTerm: string) => {
        const searchedOptions =  allOptions ? allOptions.filter((option: SettingsOptionsType) => (option.name?.toLowerCase().includes(searchTerm.toLowerCase()) || option.tags?.join(" ")?.toLowerCase()?.includes(searchTerm.toLowerCase()))) : []
        // const searchedOptions = new Set(searchedResources.map((resource: ResourceSchema) => resource.resource_type));
        return {searchedOptions, isLoadingOptions, reloadOptions}
    }

    const getResourceIconData = (resourceType: ResourceType | undefined) => {
        switch(resourceType){
            case 'kitchen':
                return {name: "Local Kitchen Space", description: "Set up a kitchen resource to allow members to book and use the space.", Icon: KitchenIcon, path: "kitchen", tags: []}
            case 'documentation':
                return {name: "Local Documentations", description: "Share guides or documents to help food entrepreneurs start and grow their businesses.", Icon: DocumentationIcon, path: "documentation", tags: []}
            case 'training':
                return {name: "Culinary Training", description: "Set up a training to help others kickstart their food business journey.", Icon: TrainingIcon, path: "training", tags: []}
            case 'funding':
                return {name: "Funding Opportunities", description: "Provide details on grants, loans, and other funding options available to help food entrepreneurs grow their businesses.", Icon: FundingIcon, path: "funding", tags: []}
            case 'expert':
                return {name: "Industry Experts", description: "List specialized experts to support food entrepreneurs. Provide key details so users can easily find and request the right guidance.", Icon: ExpertIcon, path: "expert", tags: []}
            case 'farm':
                return {name: "Local Fresh Food", description: "Add and organize your fresh produce to make it available for customers to browse and buy.", Icon: OrderIcon, path: "farm", tags: []}
            default:
                return {name: "Culinary Resource", description: "Discover kitchen communities and food incubators in your area.", Icon: CommunityIcon, path: "kitchen", tags: []}
        }
    }
    return {allOptions: allOptions ? allOptions : [], isLoadingOptions, reloadOptions, searchOptions, getResourceIconData}
}

export const useLoadResourcesFilter =  (resourceType: ResourceType, filterState: string, filterActive: boolean | undefined, filterKind?: string) => {
    // const { updateResources } = useResource()
    var { communityId } = useResourceState()
    const {data: allResources, refetch: reloadResources,  isLoading: isLoadingResources} = useQuery({ 
        queryKey: ['resources', resourceType], 
        queryFn: async () => {
            console.log("fetching filtered resources")
            const res = await getAllResources(communityId ? communityId : "", resourceType)
            // console.log("res", res)
            return res ? res : []
        },
        staleTime,
        enabled: isEnabled
    })
    const filteredResources = allResources ?  allResources.filter((resource: ResourceSchema) => (resource?.state?.toLowerCase() === filterState.toLowerCase() || resource?.state === "" || filterState === "") && (filterActive === undefined || (resource?.active === filterActive)) && (!filterKind || (resource.funding_profile?.kind || 'capital') === filterKind)) : []


    const  filterResourcesBySearchPhrase = (
        items: ResourceSchema[],
        searchPhrase: string,
        attributes: (keyof ResourceSchema)[]
    ) => {

        const lowerSearchPhrase = searchPhrase.toLowerCase();
        return items.filter(item =>
          attributes.some(attr => {
            const value = item[attr];
            return (value && typeof value === 'string') && 
                   value.toLowerCase().includes(lowerSearchPhrase);
                //    new RegExp(lowerSearchPhrase.toLowerCase()).test(value.toLowerCase());
                //    value.indexOf(lowerSearchPhrase) !== -1;
          })
        );
    }

    const searchResources = (searchTerm: string) => {
        const searchAttributes: (keyof ResourceSchema)[] = ["name", "description", "location", "zipcode"]
        const searchedResources = filterResourcesBySearchPhrase(filteredResources, searchTerm, searchAttributes)
        return {searchedResources, isLoadingResources, reloadResources}
    }

    const getPage = (resourcesList: ResourceSchema[], pageNumber:  number) => {
        var numAllResources = resourcesList?.length ? resourcesList.length : 0
        var startIndex = 0
        var endIndex = numAllResources <= resourcesPerPage ? numAllResources : resourcesPerPage
        var numResources = numAllResources
        var numPages = Math.ceil(numAllResources / resourcesPerPage)
        // var paginatedResources = filteredResources.slice(startIndex, endIndex)

        if (resourcesList && pageNumber && pageNumber > 0 && pageNumber <= numPages){
            startIndex = (pageNumber-1) * resourcesPerPage
            endIndex = (numPages === pageNumber) ? numAllResources : (pageNumber * resourcesPerPage)
        }
        var paginatedResources = resourcesList.slice(startIndex, endIndex)
        numResources = paginatedResources?.length ? paginatedResources.length : 0
        return {paginatedResources, numAllResources, numResources, startIndex, endIndex}
    }
    return {filteredResources, isLoadingResources, getPage, reloadResources, searchResources}
}



// export const useLoadPlans = () => {
//     let { communityId, setPlans } = useResourceState()
//     const {data: plans, refetch: reloadPlans, isLoading: isLoadingPlans} = useQuery({ 
//         queryKey: ["plans", communityId], 
//         queryFn: async () => {
//             if (communityId){
//                 console.log("fetching plans")
//                 var res = await getMembershipPlans(communityId)
//                 if (res && setPlans!== undefined){
//                     setPlans(res)
//                 }
//                 return res
//             }
//             return []
//         },
//         staleTime,
//         enabled: isEnabled
//     })
//     return { isLoadingPlans, plans, reloadPlans}
// }


export const useAddInventory = () => {
    // var { communityId } = useResourceState()
    // const reloadInventoryList  = useLoadInventoryList().reloadInventoryList
    // const mutation = useMutation({ 
    //     mutationFn: async (inventory: InventorySchema) => {
    //         console.log("add community inventory")
    //         if (communityId){
    //             const res = await addCommunityInventory(communityId, inventory)
    //             reloadInventoryList()
    //             return res
    //         }
    //         return null
    // }})

    // const addInventoryItem = (inventory: InventorySchema) => {
    //     mutation.mutate(inventory)
    //     return mutation?.data ? true : false
    // }

    const searchInventoryCatalog = (category: string, query: string): InventoryCatalogSuggestion[] =>
        searchIndexedInventoryCatalog(category, query);

    return { searchInventoryCatalog }
}


export const useLoadInventoryList = () => {
    let { communityId, setInventoryList } = useResourceState()
    const {data: inventoryList, refetch: reloadInventoryList, isLoading: isLoadingInventoryList} = useQuery({ 
        queryKey: ["inventory", communityId], 
        queryFn: async () => {
            if (communityId){
                console.log("fetching community inventory")
                var res = await getCommunityAllInventory(communityId)
                if (res && setInventoryList!== undefined){
                    setInventoryList(res)
                }
                return res
            }
            return []
        },
        staleTime,
        enabled: isEnabled
    })
    return { isLoadingInventoryList, inventoryList, reloadInventoryList}
}


export const useLoadRequirements = () => {
    const { setRequirements } = useResourceState()
    const { communityId } = useAuthState()
    // console.log("communityId", communityId)
    const {data: requirements, refetch: reloadRequirements, isLoading: isLoadingRequirements} = useQuery({ 
        queryKey: ['requirements', communityId], 
        queryFn: async () => {
            if (communityId){
                console.log("fetching requirements")
                return await getCommunityRequirements(communityId)
            }
            return []
        },
        staleTime,
        enabled: isEnabled
    })

    useEffect(() => {
        if (requirements && setRequirements !== undefined) {
            startTransition(() => setRequirements(requirements))
        }
    }, [requirements, setRequirements])

    // add requirement
    const addRequirementsMutation = useMutation({ 
        mutationFn: async (requirements: RequirementSchema[]) => {
            console.log("add requirement")
            const res = await addRequirements(communityId ? communityId : "", requirements)
            if (res){
                reloadRequirements()
            }
            return res
    }})
    
    const addCommunityRequirements = (requirements: RequirementSchema[]) => {
        addRequirementsMutation.mutate(requirements)
        return addRequirementsMutation?.data ? true : false
    }


    // add custom requirement
    const addCustomRequirementMutation = useMutation({ 
        mutationFn: async (requirement: RequirementSchema) => {
            console.log("add requirement")
            const res = await addMembershipRequirement(communityId ? communityId : "", requirement)
            if (res){
                reloadRequirements()
            }
            return res
    }})
    

    const addCustomRequirement = (requirement: RequirementSchema) => {
        addCustomRequirementMutation.mutate(requirement)
        return addCustomRequirementMutation?.data ? true : false
    }


        // delete requirement
        const deleteRequirementMutation = useMutation({ 
            mutationFn: async (reqId: string) => {
                console.log("delete requirement")
                const res = await deleteMembershipRequirement(communityId ? communityId : "", reqId)
                reloadRequirements()
                return res
        }})
        const deleteResourceRequirement = (reqId: string) => {
            deleteRequirementMutation.mutate(reqId)
            return deleteRequirementMutation?.data ? true : false
        }


    const isLoading = (
        addRequirementsMutation.isPending ||
        addCustomRequirementMutation.isPending ||
        deleteRequirementMutation.isPending
    );

    const isSuccess = (
        addRequirementsMutation.isSuccess ||
        addCustomRequirementMutation.isSuccess ||
        deleteRequirementMutation.isSuccess
    );

    const isError = (
        addRequirementsMutation.isError ||
        addCustomRequirementMutation.isError ||
        deleteRequirementMutation.isError
    );

        
    return { isLoadingRequirements, requirements, isLoading,isSuccess, isError, addCommunityRequirements, addCustomRequirement, deleteResourceRequirement, reloadRequirements}
}


export const useKitchenHook = () => {
    var { communityId, plans, setKitchen, setPlans } = useResourceState()
    // const { reloadPlans } = useLoadPlans()
    const { reloadRequirements } = useLoadRequirements()
    const { reloadResources } = useLoadCommunityResources()
    
    // add kitchen space
    const addKitchenMutation = useMutation({ 
        mutationFn: (kitchen: KitchenSpaceSchema | {}) => {
            console.log("add kitchen")
            const res = addKitchen(communityId ? communityId : "", kitchen)
            reloadResources()
            return res
    }})
    
    const addKitchenSpace = (kitchen: KitchenSpaceSchema | {}) => {
        addKitchenMutation.mutate(kitchen)
        return addKitchenMutation?.data ? true : false
    }

    // update kitchen space
    const updateKitchenMutation = useMutation({ 
        mutationFn: async ({spaceId, kitchenUpdates}: {spaceId: string, kitchenUpdates: KitchenSpaceSchema | ResourceSchema}) => {
            console.log("update kitchen space")
            const res = await updateKitchen({communityId: communityId ? communityId : "", spaceId, kitchenUpdates})
            if (res && setKitchen !== undefined){
                setKitchen(res)
            }
            return res
    }})
    const updateKitchenSpace = (spaceId: string, kitchenUpdates: KitchenSpaceSchema | ResourceSchema) => {
        updateKitchenMutation.mutate({spaceId, kitchenUpdates})
        reloadResources()
        return updateKitchenMutation?.data ? true : false
    }

    // add kitchen plan
    const addPlanMutation = useMutation({ 
        mutationFn: async (plan: PlanSchema | {}) => {
            console.log("add plan")
            const res = await addCommunityPlans(communityId ? communityId : "", plan)
            if (res && setPlans !== undefined){
                let updated_plans: any = (plan && plans) ? [...plans] : []
                updated_plans.push(res)
                setPlans(updated_plans)
            }
            return res
    }})
    const addKitchenPlan = (plan: PlanSchema | {}) => {
        addPlanMutation.mutate(plan)
        return addPlanMutation?.data ? true : false
    }

    // update kitchen plan
    // const updatePlanMutation = useMutation({ 
    //     mutationFn: async ({planId, planUpdate}: {planId: string, planUpdate: PlanSchema}) => {
    //         console.log("update plan")
    //         const res = await updatePlan(communityId ? communityId : "", planUpdate, planId)
    //         reloadPlans()
    //         return res
    // }})

    // const updateKitchenPlan = (planId: string, planUpdate: PlanSchema) => {
    //     updatePlanMutation.mutate({planId, planUpdate})
    //     return updatePlanMutation?.data ? true : false
    // }

    // add requirement
    const addRequirementMutation = useMutation({ 
        mutationFn: async (requirement: RequirementSchema | {}) => {
            console.log("add requirement")
            const res = await addMembershipRequirement(communityId ? communityId : "", requirement)
            if (res){
                // let updated_reqs: RequirementSchema[] = (requirement && requirements) ? [...requirements] : []
                // updated_reqs.push(res)
                // setRequirements(updated_reqs)
                reloadRequirements()
            }
            return res
    }})
    const addKitchenRequirement = (requirement: RequirementSchema | {}) => {
        addRequirementMutation.mutate(requirement)
        return addRequirementMutation?.data ? true : false
    }

    // delete requirement
    const deleteRequirementMutation = useMutation({ 
        mutationFn: async (req_id: string) => {
            console.log("delete requirement")
            const res = await deleteMembershipRequirement(communityId ? communityId : "", req_id)
            reloadRequirements()
            return res
    }})
    const deleteKitchenRequirement = (req_id: string) => {
        deleteRequirementMutation.mutate(req_id)
        return deleteRequirementMutation?.data ? true : false
    }

    // update kitchen options
    const updateKitchenOptionsMutation = useMutation({ 
        mutationFn: async ({spaceId, optionsUpdates}: {spaceId: string, optionsUpdates: KitchenOptionsType | ResourceOptionType}) => {
            console.log("update kitchen space")
            const res = await updateKitchenOptions(communityId ? communityId : "", spaceId, optionsUpdates)
            if (res && setKitchen !== undefined){
                setKitchen(res)
            }
            return res
    }})

    const updateOptions = (spaceId: string, optionsUpdates: KitchenOptionsType | ResourceOptionType) => {
        updateKitchenOptionsMutation.mutate({spaceId, optionsUpdates})
        return updateKitchenMutation?.data ? true : false
    }

    const isLoading = (
        addKitchenMutation.isPending ||
        updateKitchenMutation.isPending ||
        addPlanMutation.isPending || 
        // updatePlanMutation.isPending || 
        addRequirementMutation.isPending || 
        deleteRequirementMutation.isPending ||
        updateKitchenOptionsMutation.isPending
        // || isLoadingCommunity
    );

    return { isLoading, addKitchenSpace, addKitchenPlan, addKitchenRequirement, updateKitchenSpace, updateOptions, deleteKitchenRequirement }
}



export const useLoadResourceInventoryItems = (resourceId: string, queryEnabled = true, syncContext = true) => {
    let { communityId, setInventoryList } = useResourceState()
    const {data: inventoryList, refetch: reloadInventoryList, isLoading: isLoadingInventoryList} = useQuery({ 
        queryKey: ["inventory", communityId, resourceId],
        queryFn: async () => {
            if (communityId){
                console.log("fetching resource inventory items")
                var res = await getAllResourceInventory(communityId, resourceId)
                if (res && syncContext && setInventoryList!== undefined){
                    setInventoryList(res)
                }
                return res
            }
            return []
        },
        staleTime,
        enabled: isEnabled && queryEnabled && Boolean(resourceId)
    })
    return { isLoadingInventoryList, inventoryList, reloadInventoryList}
}

type hookPropsType = {
    resourceType?: ResourceType, 
    filterState?: string, 
    filterActive?: boolean | undefined, 
    resourceId?: string
}

export const useResourceHook = (hookProps: hookPropsType) => {
    const {resourceType, filterState, filterActive, resourceId} = hookProps
    var { communityId } = useResourceState()
    const { reloadCurrentResource } = useLoadCurrentResource(resourceId ? resourceId : '')
    const { reloadPlans } = useLoadPlans()

    const { reloadResources } = useLoadResourcesFilter(
                                    resourceType ? resourceType : "" as ResourceType, 
                                    filterState ? filterState : "", 
                                    filterActive ? filterActive : undefined
                                )
    
    // add resource
    const addResourceMutation = useMutation({ 
        mutationFn: async (resource: ResourceSchema | {}) => {
            console.log("add resource")
            const res = await addNewResource(communityId ? communityId : "", resource)
            await reloadResources()
            return res
    }})
    
    const addResource = (resource: ResourceSchema | {}) => {
        addResourceMutation.mutate(resource)
        return addResourceMutation?.data ? true : false
    }

    // update resource
    const updateResourceMutation = useMutation({ 
        mutationFn: async ({resourceId, resourceUpdates}: {resourceId: string, resourceUpdates: ResourceSchema}) => {
            console.log("update resource")
            const res = await updateResourceById(communityId ? communityId : "", resourceId, resourceUpdates)
            // if (res && setCurrentResource !== undefined){
            //     await reloadCurrentResource()
            // }
            // console.log("update resource response", res)
            return res
        },
        onSuccess: async () => {
            console.log("update resource success")
            await reloadCurrentResource()
        },
        onError: async (error) => {
            console.log("update resource error", error)
        }
    })
    const updateResource = async (resourceId: string, resourceUpdates: ResourceSchema) => {
        const res = await updateResourceMutation.mutateAsync({resourceId, resourceUpdates})
        return res ? true : false
    }

    // delete resource
    const deleteResourceMutation = useMutation({ 
        mutationFn: async () => {
            console.log("delete resource")
            const res = await deleteCommunityResource(communityId ? communityId : "", resourceId ? resourceId : "")
            await reloadResources()
            return res
    }})
    
    const deleteResource = () => {
        deleteResourceMutation.mutate()
        return deleteResourceMutation?.data ? true : false
    }

    // add community plan
    const addPlanMutation = useMutation({ 
        mutationFn: async (plan: PlanSchema | {}) => {
            console.log("add membership plan")
            const res = await addCommunityPlans(communityId ? communityId : "", plan)
            // await reloadPlans()
            // if (res && setPlans !== undefined){
            //     let updated_plans: any = (plan && plans) ? [...plans] : []
            //     updated_plans.push(res)
            //     setPlans(updated_plans)
            // }
            return res
        },
        onSuccess: async () => {
            await reloadPlans()
        }
    })
    const addCommunityPlan = async (plan: PlanSchema | {}) => {
        try {
            const res = await addPlanMutation.mutateAsync(plan)
            return !!res
        } catch (e) {
            return false
        }
    }

    // add resource fee
    const addResourceFeeMutation = useMutation({ 
        mutationFn: async ({resourceId, fee}: {resourceId: string, fee: FeeSchema}) => {
            console.log("add fee")
            const res = await updateResourceFee(communityId ? communityId : "", resourceId, fee)
            if (res){
                await reloadCurrentResource()
            }
            return res  
    }}) 

    const addNewResourceFee = (resourceId: string, fee: FeeSchema) => {
        addResourceFeeMutation.mutate({resourceId, fee})
        return addResourceFeeMutation?.data ? true : false
    }

    // delete resource fee
    const deleteResourceFeeMutation = useMutation({ 
        mutationFn: async ({resourceId, feeId}: {resourceId: string, feeId: string}) => {
            console.log("delete fee")
            const res = await deleteResourceFee(communityId ? communityId : "", resourceId, feeId)
            if (res){
                await reloadCurrentResource()
            }
            return res
    }})

    const deleteExistingResourceFee = (resourceId: string, feeId: string) => {
        deleteResourceFeeMutation.mutate({resourceId, feeId})
        return deleteResourceFeeMutation?.data ? true : false
    }

    // add resource discount
    const addResourceDiscountMutation = useMutation({ 
        mutationFn: async ({resourceId, discount}: {resourceId: string, discount: DiscountSchema}) => {
            console.log("add discount")
            const res = await updateResourceDiscount(communityId ? communityId : "", resourceId, discount)
            return res  
        },
        onSuccess: async () => {
            await reloadCurrentResource()
        }
    }) 


    const addNewResourceDiscount = async (resourceId: string, discount: DiscountSchema) => {
        const res = await addResourceDiscountMutation.mutateAsync({resourceId, discount})
        return !!res
    }


    // delete resource discount
    const deleteDiscountMutation = useMutation({ 
        mutationFn: async ({resourceId, discountName}: {resourceId: string, discountName: string}) => {
            console.log("delete discount")
            const res = await deleteResourceDiscount(communityId ? communityId : "", resourceId, discountName)
            if (res){
                await reloadCurrentResource()
            }
            return res  
    }}) 

    const deleteExistingDiscount = (resourceId: string, discountName: string) => {
        deleteDiscountMutation.mutate({resourceId, discountName})
        return deleteDiscountMutation?.data ? true : false
    }

    // update resource plans
    const updateResourcePlansMutation = useMutation({ 
        mutationFn: async (plans: string[]) => {
            communityId = communityId ? communityId : ""
            const res = await updatePlansForResource(communityId, resourceId ? resourceId : "", plans)
            // await reloadPlans()
            return res
    }})

    const updateResourcePlans = (plans: string[]) => {
        updateResourcePlansMutation.mutate(plans)
        return updateResourcePlansMutation?.data ? true : false
    }

    // add resource plan
    const addResourcePlanMutation = useMutation({ 
        mutationFn: async ({resourceId, plan}: {resourceId: string, plan: ResourcePlanSchema}) => {
            console.log("add resource plan")
            const res = await addResourcePlan(communityId ? communityId : "", resourceId ? resourceId : "", plan)
            // Intentionally do not call reload here; use onSuccess to ensure success state updates
            return res
        },
        onSuccess: async () => {
            await reloadCurrentResource()
        }
    })


    const addPlanForResource = async (resourceId: string, plan: ResourcePlanSchema) => {
        try {
            const res = await addResourcePlanMutation.mutateAsync({resourceId, plan})
            return !!res
        } catch (e) {
            return false
        }
    }    

    // update resource plan
    const updatePlanMutation = useMutation({ 
        mutationFn: async ({planId, planUpdate}: {planId: string, planUpdate: ResourcePlanSchema}) => {
            console.log("update plan")
            communityId = communityId ? communityId : "" as string
            const res = await updatePlan(communityId, "resource", planId, planUpdate)
            // console.log("update resource plan response", res)
            // reloadPlans()
            return res
        },
        onSuccess: async () => {
            await reloadCurrentResource()
        }
    })


    const updateResourcePlan = async (planId: string, planUpdate: ResourcePlanSchema) => {
        const result = await updatePlanMutation.mutateAsync({planId, planUpdate})
        // console.log("update resource plan result", result)
        return result ? true : false
    }

    // delete plan
    const deletePlanMutation = useMutation({ 
        mutationFn: async ({planId}: {planId: string, planType: PlanType}) => {
            console.log("delete plan")
            const res = await deleteMembershipPlan(communityId ? communityId : "", planId)
            // Defer refetch to onSuccess for reliable status
            return res
        },
        onSuccess: async () => {
            // Dual tiers shown under a resource are sourced from the community plans
            // list, so always refresh it on delete to drop the removed tier from the
            // merge source immediately (not just for membership-type deletes).
            await reloadPlans()
        }
    })

    const deletePlan = async (planId: string, planType: PlanType) => {
        try {
            const res = await deletePlanMutation.mutateAsync({planId, planType})
            return !!res
        } catch (e) {
            return false
        }
    }
    
    // update resource requirements
    const updateResourceRequirementsMutation = useMutation({ 
        mutationFn: async (requirements: string[]) => {
            communityId = communityId ? communityId : ""
            const res = await updateRequirementsForResource(communityId, resourceId ? resourceId : "", requirements)
            return res
        },
        onSuccess: async () => {
            await reloadCurrentResource()
        }
    })
    const updateResourceRequirements = (requirements: string[]) => {
        updateResourceRequirementsMutation.mutate(requirements)
        return updateResourceRequirementsMutation?.data ? true : false
    }


    // update resource options
    const updateResourceOptionsMutation = useMutation({ 
        mutationFn: async ({resourceId, optionsUpdates}: {resourceId: string, optionsUpdates: ResourceOptionType}) => {
            const res = await updateResourceOptions(communityId ? communityId : "", resourceId, optionsUpdates)
            await reloadCurrentResource()
            return res
    }})

    const updateOptions = (resourceId: string, optionsUpdates: ResourceOptionType) => {
        updateResourceOptionsMutation.mutate({resourceId, optionsUpdates})
        return updateResourceMutation?.data ? true : false
    }

    const updateResourceRulesMutation = useMutation({ 
        mutationFn: async (rules: string[]) => {
            const res = await updateRulesForResource(communityId ? communityId : "", resourceId ? resourceId : "", rules)
            // await reloadCurrentResource()
            return res
        },
    
        onSuccess: async () => {
            await reloadCurrentResource()
        }
    })
    
    const updateResourceRules = async (rules: string[]) => {
        const result = await updateResourceRulesMutation.mutateAsync(rules)
        console.log("update resource rules result", result)
        return result ? true : false
    }

    const updateDocumentationStepsMutation = useMutation({
        mutationFn: async (steps: DocumentationStep[]) => {
            const res = await updateResourceSteps(communityId ? communityId : "", resourceId ? resourceId : "", steps)
            return res
        },
        onSuccess: async () => {
            await reloadCurrentResource()
        },
    })

    /** Persist documentation steps and guarantee each step has at least one task. */
    const updateDocumentationSteps = async (steps: DocumentationStep[]) => {
        const result = await updateDocumentationStepsMutation.mutateAsync(steps)
        return result ? true : false
    }

    
    const addInventoryItemMutation = useMutation({ 
        mutationFn: async ({resourceId, inventoryItem}: {resourceId: string, inventoryItem: InventorySchema | TrainingInventorySchema}) => {
            console.log("add resource inventory item")
            // if (communityId){
            communityId = communityId ? communityId : ""
            const res = await addResourceInventory(communityId, resourceId, inventoryItem)
            reloadCurrentResource()
            return res
            // }
    }})

    const addInventoryItem = (resourceId: string, inventoryItem: InventorySchema | TrainingInventorySchema) => {
        addInventoryItemMutation.mutate({resourceId, inventoryItem})
        return addInventoryItemMutation?.data ? true : false
    }

    // update inventory item
    const updateInventoryItemByIdMutation = useMutation({ 
        mutationFn: async ({resourceId, itemId, inventoryItem}: {resourceId: string, itemId: string, inventoryItem: InventorySchema | TrainingInventorySchema}) => {
            console.log("update inventory item")
            const res = await updateResourceInventory(communityId ? communityId : "", resourceId, itemId, inventoryItem)
            return res
        },
        onSuccess: async () => {
            await reloadCurrentResource()
        },
        onError: async (error) => {
            console.error("update inventory item error", error)
        }
    })

    const updateInventoryItemById = async (resourceId: string, itemId: string, inventoryItem: InventorySchema | TrainingInventorySchema) => {
        const res = await updateInventoryItemByIdMutation.mutateAsync({resourceId, itemId, inventoryItem})
        return res ? true : false
    }

    // delete inventory item
    const deleteInventoryItemMutation = useMutation({ 
        mutationFn: async ({resourceId, itemId}: {resourceId: string, itemId: string}) => {
            console.log("delete inventory item")
            const res = await deleteInventoryItemById(communityId ? communityId : "", resourceId, itemId)
            reloadResources()
            return res
    }})

    const deleteInventoryItem = (resourceId: string, itemId: string) => {
        deleteInventoryItemMutation.mutate({resourceId, itemId})
        reloadCurrentResource()
        return deleteInventoryItemMutation?.data ? true : false
    }

    
    // update resource status
    const updateResourceStatusMutation = useMutation({ 
        mutationFn: async ({resourceId, resourceUpdates}: {resourceId: string, resourceUpdates: ResourceSchema}) => {
            console.log("update resource status")
            const res = await updateResourceById(communityId ? communityId : "", resourceId, resourceUpdates)
            if (res){
                await reloadResources()
            }
            return res
    }})
    const updateResourceStatus = async (resourceId: string, currentStatus: boolean) => {
        const resourceUpdates = {active: !currentStatus} as ResourceSchema
        updateResourceStatusMutation.mutate({resourceId, resourceUpdates})
        return updateResourceStatusMutation?.data ? true : false
    }

    const isLoading = (
        addResourceMutation.isPending ||
        updateResourceMutation.isPending ||
        updateDocumentationStepsMutation.isPending ||
        addPlanMutation.isPending || 
        updateResourcePlansMutation.isPending ||
        addResourcePlanMutation.isPending ||
        deletePlanMutation.isPending ||
        updatePlanMutation.isPending || 
        updateResourceOptionsMutation.isPending ||
        deleteResourceMutation.isPending ||
        updateResourceStatusMutation.isPending ||
        addResourceFeeMutation.isPending ||
        deleteDiscountMutation.isPending ||
        deleteResourceFeeMutation.isPending ||
        updateInventoryItemByIdMutation.isPending
        // || isLoadingCommunity
    );

    const isSuccess = (
        addResourceMutation.isSuccess ||
        updateResourceMutation.isSuccess ||
        updateDocumentationStepsMutation.isSuccess ||
        addPlanMutation.isSuccess || 
        updateResourcePlansMutation.isSuccess ||
        addResourcePlanMutation.isSuccess ||
        deletePlanMutation.isSuccess ||
        updatePlanMutation.isSuccess || 
        // deleteRequirementMutation.isSuccess ||
        updateResourceOptionsMutation.isSuccess ||
        deleteResourceMutation.isSuccess ||
        updateResourceStatusMutation.isSuccess ||
        addResourceFeeMutation.isSuccess ||
        deleteDiscountMutation.isSuccess ||
        deleteResourceFeeMutation.isSuccess ||
        updateInventoryItemByIdMutation.isSuccess
    )

    const isError = (
        addResourceMutation.isError ||
        updateResourceMutation.isError ||
        updateDocumentationStepsMutation.isError ||
        addPlanMutation.isError || 
        addResourcePlanMutation.isError ||
        deletePlanMutation.isError ||
        updatePlanMutation.isError || 
        // deleteRequirementMutation.isError ||
        updateResourceOptionsMutation.isError ||
        deleteResourceMutation.isError ||
        updateResourceStatusMutation.isError ||
        addResourceFeeMutation.isError ||
        deleteDiscountMutation.isError ||
        deleteResourceFeeMutation.isError ||
        updateInventoryItemByIdMutation.isError
    )

    return { 
        isLoading, isError, isSuccess, reloadCurrentResource, 
        addResource, addCommunityPlan, updateResourcePlans, deletePlan, addNewResourceFee,
        addNewResourceDiscount, deleteExistingDiscount, deleteExistingResourceFee,
        addInventoryItem, updateResourceRequirements,
        updateResource, updateResourceStatus, updateOptions, updateResourceRules,
        updateDocumentationSteps,
        addPlanForResource,updateResourcePlan, 
        deleteResource, deleteInventoryItem, updateInventoryItemById 
    }
}

export const useGetKitchenPlan = (planId: string) => {
    let { communityId } = useResourceState()
    const {data: plans, refetch: reloadPlans, isLoading: isLoadingPlan} = useQuery({ 
        queryKey: ["plan", communityId, planId], 
        queryFn: async () => {
            if (communityId){
                console.log("fetching plan by id")
                var res = await getMembershipPlan(communityId, planId)
                return res
            }
            return []
        },
        staleTime,
        enabled: isEnabled
    })
    return {isLoadingPlan, plans, reloadPlans}
}